import { useRef, useState } from "react"
import BaseMap, { BaseMapRef } from "./BaseMap"
import JobMarkerLayer from "./layers/JobMarkerLayer"
import MetroLayers from "./layers/MetroLayers"
import TechMarkersLayer from "./layers/TechMarkersLayer"
import RouteLayer from "./layers/RouteLayer"
import TrafficLayer from "./layers/TrafficLayer"
import JobTechsMapControls from "./JobTechsMapControls"
import { HereGeocodePosition } from "../../models/MapModels"
import Technician from "../../modules/technicians/models/technician"
import JobMetro from "../../modules/crm/base/models/JobMetro"
import { useJobTechsView } from "./useJobTechsView"

interface IProps {
    /** The job's address. */
    jobPosition?: HereGeocodePosition
    jobTitle?: string
    /** The technicians who may take this job (the list beside the map). Anybody else is shown stepped back. */
    techs?: Technician[]
    selectedTech?: Technician
    /** The technician whose row is under the pointer in the list. */
    hoveredTechId?: string
    metros?: JobMetro[]
    selectedMetro?: JobMetro
    onSelectMetro?: (metro: JobMetro) => void
    onTechClick?: (tech: Technician) => void
    /** What the route to the picked technician should say, when the list already says it. */
    routeLabel?: string
    height?: number | string
}

/**
 * The map beside the technician list when a job is given a technician: the job, the metros'
 * areas to pick from, and every technician where they are -- those who may take the job in
 * full colour, everybody else stepped back. Picking a technician draws their driving route to
 * the job with the time and miles on it; pointing at one in the list lifts their pin.
 */
export default function TechAssignMap(props: IProps) {

    const mapRef = useRef<BaseMapRef>(null)
    const [mapReady, mapReadySet] = useState<boolean>(false)
    const view = useJobTechsView({ mapRef, mapReady, jobPosition: props.jobPosition, techs: props.techs, selectedTech: props.selectedTech })
    // The traffic layer: off until asked for -- every tile of it is paid for.
    const [traffic, trafficSet] = useState<boolean>(false)
    // The metros' areas: on until switched off.
    const [metros, metrosSet] = useState<boolean>(true)

    return (
        <BaseMap ref={mapRef} height={props.height} center={props.jobPosition} zoom={10}
            onReady={() => mapReadySet(true)}>
            {metros && <MetroLayers metros={props.metros} selectedMetroId={props.selectedMetro?.id} onSelect={props.onSelectMetro} labels />}
            <TechMarkersLayer techs={view.allTechs} live
                selectedTechId={props.selectedTech?.id}
                hoveredTechId={props.hoveredTechId}
                dimmedTechIds={view.dimmed}
                onTechClick={props.onTechClick} />
            <RouteLayer from={view.selectedPos} to={props.jobPosition} label={props.routeLabel} onRoute={view.fitRoute} />
            <JobMarkerLayer position={props.jobPosition} title={props.jobTitle} onClick={view.showJobAndPick} />
            {traffic && <TrafficLayer />}
            <JobTechsMapControls onJobAndPick={view.showJobAndPick} onEveryone={view.showEveryone}
                metros={metros} onMetros={() => metrosSet(m => !m)}
                traffic={traffic} onTraffic={() => trafficSet(t => !t)} />
        </BaseMap>
    )
}
